import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ScrollText, Filter } from "lucide-react";
import TableExportButton from "@/components/TableExportButton";

const ACTIONS = [
  { value: "", label: "All actions" },
  { value: "login", label: "Login" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "approve", label: "Approve" },
  { value: "reject", label: "Reject" },
  { value: "generate_po", label: "Generate PO" },
];

const ENTITY_TYPES = [
  { value: "", label: "All entities" },
  { value: "indent", label: "Indent" },
  { value: "purchase_order", label: "Purchase Order" },
  { value: "intender", label: "Intender" },
  { value: "consumable", label: "Consumable" },
  { value: "supplier", label: "Supplier" },
  { value: "settings", label: "Settings" },
  { value: "auth", label: "Auth" },
];

const ACTION_COLORS = {
  login: "bg-slate-100 text-slate-700",
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-amber-100 text-amber-700",
  delete: "bg-rose-100 text-rose-700",
  approve: "bg-emerald-100 text-emerald-700",
  reject: "bg-rose-100 text-rose-700",
  generate_po: "bg-[#1D2951]/10 text-[#1D2951]",
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (action) params.set("action", action);
    if (entityType) params.set("entity_type", entityType);
    api.get(`/audit-logs?${params.toString()}`).then((r) => setLogs(r.data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, entityType]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">System</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            Audit Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Immutable record of every key action across the workspace.
          </p>
        </div>
        <ScrollText size={28} className="text-slate-400" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-center gap-3">
        <Filter size={14} className="text-slate-500" />
        <select data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)} className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white">
          {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <select data-testid="audit-entity-filter" value={entityType} onChange={(e) => setEntityType(e.target.value)} className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white">
          {ENTITY_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <span className="text-xs text-slate-500 ml-auto font-mono">{logs.length} entries</span>
        <TableExportButton
          testIdPrefix="audit-export"
          filename="audit_log"
          rows={logs.map((l) => ({
            timestamp: l.timestamp || "",
            actor: l.actor_name || l.actor || "",
            action: l.action || "",
            entity_type: l.entity_type || "",
            entity_id: l.entity_id || "",
            detail: l.detail || "",
          }))}
          dateField="timestamp"
          columns={[
            { key: "timestamp", label: "Timestamp" },
            { key: "actor", label: "Actor" },
            { key: "action", label: "Action" },
            { key: "entity_type", label: "Entity" },
            { key: "entity_id", label: "Entity ID" },
            { key: "detail", label: "Detail" },
          ]}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <th className="text-left px-6 py-3">Timestamp</th>
                <th className="text-left px-6 py-3">User</th>
                <th className="text-left px-6 py-3">Action</th>
                <th className="text-left px-6 py-3">Entity</th>
                <th className="text-left px-6 py-3">Summary</th>
              </tr>
            </thead>
            <tbody data-testid="audit-list">
              {logs.length === 0 && (
                <tr><td colSpan="5" className="text-center py-12 text-slate-400">No audit entries match your filters.</td></tr>
              )}
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-6 py-3">
                    <div className="font-medium text-slate-900">{l.user_name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{l.user_role || "—"}</div>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${ACTION_COLORS[l.action] || "bg-slate-100 text-slate-700"}`}>
                      {l.action}
                    </span>
                  </td>
                  <td className="px-6 py-3 font-mono text-xs">{l.entity_type}</td>
                  <td className="px-6 py-3 text-slate-700">{l.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
